<template>
  <v-layout
    row
    justify-center
  >
    <v-dialog
      v-model="isOpen"
      persistent
      max-width="1000px"
    >
      <v-card class="create-bridge-signing">
        <v-form>
          <v-card-title class="toolbar primary white--text">
            <v-icon dark>
              note_add
            </v-icon>
            <h3
              class="title ml-2"
            >
              {{ !item.user_id ? 'Create' : 'Update' }} Bank Information
            </h3>
            <v-spacer />
            <v-btn
              color="white"
              flat
              @click="onToggleOpen"
            >
              <v-icon>
                disabled_by_default
              </v-icon>
            </v-btn>
          </v-card-title>
          <v-progress-linear
            v-if="isLoading"
            :indeterminate="true"
            class="ma-0"
          />

          <v-card-text>
            <v-container
              grid-list-md
              class="pa-0"
            >
              <v-layout
                wrap
                class="form-group"
              >
                <v-flex xs12>
                  <h6 class="subheading">
                    Bank Information
                  </h6>
                  <p>
                    By updating the IBAN / BIC,
                    the "IBAN Updated At" will be automatically updated to the current date.
                  </p>
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <template v-if="item.user_id">
                    <UserChip
                      :id="item.user_id"
                    /> {{ item.user_id }}
                  </template>
                  <template v-else>
                    <v-text-field
                      v-model="formData.user_id"
                      label="User ID"
                      :disabled="!!item.user_id"
                      :error-messages="getFieldErrors('user_id')"
                      @change="$v.formData.user_id.$touch()"
                      @blur="$v.formData.user_id.$touch()"
                    />
                  </template>
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-text-field
                    v-model="formData.signing_updated_at"
                    label="IBAN Updated At"
                    disabled="true"
                    :error-messages="getFieldErrors('signing_updated_at')"
                    @change="$v.formData.signing_updated_at.$touch()"
                    @blur="$v.formData.signing_updated_at.$touch()"
                  />
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-text-field
                    v-model="formData.iban"
                    label="IBAN"
                    :error-messages="getFieldErrors('iban')"
                    mask="NNNN NNNN NNNN NNNN NNNN NNNN NNNN"
                    @change="$v.formData.iban.$touch()"
                    @blur="$v.formData.iban.$touch()"
                  />
                </v-flex>
                <v-flex
                  xs12
                  md6
                >
                  <v-text-field
                    v-model="formData.bic"
                    label="BIC"
                    :error-messages="getFieldErrors('bic')"
                    @change="$v.formData.bic.$touch()"
                    @blur="$v.formData.bic.$touch()"
                  />
                </v-flex>
              </v-layout>
            </v-container>
            <v-alert
              v-for="e in error"
              :key="e.path"
              :value="error"
              type="error"
              color="error"
            >
              {{ e.message }}
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              color="secondary"
              flat
              @click="onToggleOpen"
            >
              Cancel
            </v-btn>
            <v-btn
              :disabled="isLoading || !$v.formData.$anyDirty"
              :loading="isLoading"
              color="primary"
              @click="submit"
            >
              <span>
                {{ !item.user_id ? 'Create' : 'Update' }} Bank Information
              </span>
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
  </v-layout>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import UserChip from '@/components/shared/user/UserChip';

export default {
  name: 'CreateDialog',
  components: {
    UserChip,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    isOpen: {
      type: Boolean,
      required: true,
    },
    toggleOpen: {
      type: Function,
      required: true,
    },
  },
  data: () => ({
    isOpenProp: this.isOpen,
    isLoading: false,
    error: null,
    formData: {
      user_id: '',
      iban: '',
      bic: '',
      signing_updated_at: '',
    },
  }),
  validations() {
    return {
      formData: {
        user_id: { required },
        iban: { required },
        bic: { },
        signing_updated_at: {},
      },
    };
  },
  computed: {
    validFields() {
      return Object.keys(this.$v.formData.$params).filter(
        item => !this.$v.formData[item].$invalid,
      );
    },
    errors() {
      return Object.keys(this.$v.formData.$params).filter(
        item => this.$v.formData[item].$invalid,
      );
    },
  },
  mounted() {
    this.formData = {
      ...this.formData,
      ...(this.serializeItem(this.item)),
    };

    this.$v.$touch();
  },
  methods: {
    getFieldErrors(field) {
      const errors = [];
      const formField = this.getProperty(this.$v.formData, field);

      if (formField) {
        if (!formField.$dirty || !formField.$invalid) {
          return errors;
        }

        if (!formField.required) {
          errors.push('Required field');
        }

        if (!formField.minLength) {
          errors.push('Field has a minimum length');
        }

        if (!formField.maxLength) {
          errors.push('Field has a maximum length');
        }
      }

      return errors;
    },
    getProperty(object, propString) {
      if (!propString) {
        return object;
      }

      let prop;
      const props = propString.split('.');

      let i;
      const iLen = props.length - 1;
      // eslint-disable-next-line no-plusplus
      for (i = 0; i < iLen; i++) {
        prop = props[i];
        const candidate = object[prop];
        if (candidate !== undefined) {
          object = candidate;
        } else {
          break;
        }
      }

      return object[props[i]];
    },
    onToggleOpen() {
      this.toggleOpen();
      this.error = null;
    },
    submit() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }

      this.isLoading = true;
      if (this.item.user_id) {
        this.$store
          .dispatch('updatePatBankInformation', this.serializeFormData(this.formData))
          .then(() => {
            this.onToggleOpen();
          })
          .catch((err) => {
            this.error = err;
          })
          .finally(() => {
            this.isLoading = false;
          });
      } else {
        this.$store
          .dispatch('createPatBankInformation', this.serializeFormData(this.formData))
          .then(() => {
            this.onToggleOpen();
          })
          .catch((err) => {
            this.error = err;
          })
          .finally(() => {
            this.isLoading = false;
          });
      }
    },
    serializeItem(item) {
      const { ...serializeItem } = item;

      return {
        ...serializeItem,
      };
    },
    serializeFormData(formData) {
      const { ...serializeFormData } = formData;

      return {
        ...serializeFormData,
      };
    },
  },
};
</script>

<style lang="scss">
.create-bridge-signing {

  .v-card__text {
    max-height: 65vh;
    overflow: auto;
    padding-top: 32px;
  }

  .v-card__actions {
    border-top: 1px solid rgba(0, 0, 0, 0.12);
  }

  .subheading {
    padding: 8px;
    background: white;
    color: rgba(0, 0, 0, 0.54);
    top: -20px;
    left: 8px;
    position: absolute;
    display: flex;
    align-items: center;
  }

  .form-group {
    padding: 16px;
    margin: 0 0 24px 0;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 5px;
    position: relative;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/payoutsAndTaxes/payout/dialogs/bankInformation/CreateDialog.vue